'use client';
import { useSite, useT } from '../../lib/i18n';
import { pickName } from '../../lib/i18n-core';
import type { ProductListItem } from '../../lib/site';
import { Icon } from '../Icon';
import { WishButton } from './WishButton';
import { CourseLinks } from './CourseLinks';
import { PriceFooter, PriceOptions, usePrice } from './PriceBlock';
import { Thumb } from './Thumb';
import { BookChips, CourseChips } from './CourseCard';
import { badgeClass, typeLabel } from './labels';
import { SmartLink } from '../SmartLink';

/**
 * 리스트 한 줄 — 가이드 `clist-item`.
 *
 * **형태는 설정이 고른다**(`listStyle.listview_list`) —
 *  - `detail` : 썸네일·배지·제목·강사·차시/기간·찜·맛보기·가격·담기/신청
 *  - `simple` : 배지·제목·가격만(가이드 `clist-item-sp`)
 */
export function CourseRow({ p, variant, priceDetail, freeVod, hoverPreview, href, billing }: {
  p: ProductListItem;
  variant: 'detail' | 'simple';
  priceDetail: boolean;
  freeVod: boolean;
  /**
   * 미리보기 재생(설정 `listStyle.hoverPreview`) — 카드와 **같은 설정**을 따른다.
   * 간단형은 썸네일 자체가 없어(가이드 `clist-item-sp`) 붙일 자리가 없다.
   */
  hoverPreview?: boolean;
  href: string;
  /** 구독 보기 — 가격을 지우고 [수강하기] 하나만 둔다(`PriceFooter.billing`) */
  billing?: boolean;
}) {
  const t = useT();
  const { lang } = useSite();
  const teacher = p.displayTeachers[0]?.teacher;
  const price = usePrice(p, priceDetail);
  const category = p.categories.length ? pickName(p.categories[p.categories.length - 1].name, lang) : null;

  if (variant === 'simple') {
    /*
     * 가이드 `clist-item-sp` 를 그대로 따른다(2026-08-22 사용자 지적으로 전수 대조).
     * 예전에는 배지·제목·가격만 그렸는데, 가이드의 간단형은 **썸네일과 담기/신청 버튼만**
     * 빠진 것이지 강사·차시 배지·맛보기·찜까지 빠진 것이 아니다.
     * 자세히와 다른 점 둘 — ①썸네일이 없다 ②찜이 링크 줄이 아니라 **값 위**에 온다.
     */
    return (
      <div className="clist-item clist-item-sp">
        <div className="clist-info mr20">
          <div className="clist-badges mb5">
            <span className={`badge-type ${badgeClass(p.type)} filter`}>{typeLabel(p.type, t)}</span>
          </div>
          <p className="clist-title"><SmartLink href={href}>{p.name}</SmartLink></p>
          {(teacher || category) && (
            <p className="course-teacher mt3">
              <Icon name="user" /> {[teacher?.name, category].filter(Boolean).join(' · ')}
            </p>
          )}
          <CourseChips p={p} className="mt5" />
        </div>
        <div className="clist-links">
          <CourseLinks p={p} freeVod={freeVod} teacherName={teacher?.name} />
        </div>
        <div className="clist-actions">
          <WishButton productId={p.id} wished={p.wished} className="clist-wish" />
          {/* 간단형은 담기·신청 버튼과 할인 표기가 없다 — 값(또는 단가 목록)만 */}
          <PriceFooter billing={billing} p={p} line={price.line} href={href} actions={false} block priceOnly lines={price.lines}
            showSingle={priceDetail} />
        </div>
      </div>
    );
  }

  /*
   * **교재는 줄도 다르다**(가이드 `book-list.html` — 2026-08-22 사용자 지적).
   * 강사·차시/기간 대신 **저자**와 교재 칩이 오고, 맛보기·관련교재 링크가 없으며
   * 찜이 링크 줄이 아니라 **제 칸**에 있고, 버튼이 `구매하기` 다.
   */
  if (p.type === 'BOOK') {
    return (
      <div className="clist-item">
        {/* 교재 리스트만 `clist-thumb` 다 — 가이드 `book-list.html` 그대로 */}
        <Thumb image={p.image} name={p.name} href={href} className="clist-thumb" />
        <div className="clist-info">
          {category && (
            <div className="clist-badges mb5">
              <span className="badge-type bg-blue filter">{category}</span>
            </div>
          )}
          <p className="clist-title"><SmartLink href={href}>{p.name}</SmartLink></p>
          {teacher && <p className="course-teacher mt3"><Icon name="user" /> {t('prd.author', { name: teacher.name ?? '' })}</p>}
          <BookChips p={p} className="mt5" />
        </div>
        {/* 찜은 `clist-actions` 안 첫 자식이다(2026-08-25 가이드 개정 — book-list.html) */}
        <div className="clist-actions">
          <WishButton productId={p.id} wished={p.wished} className="clist-wish" />
          <PriceFooter billing={billing} p={p} line={price.line} href={href} block buyLabel={t('prd.buy')} />
        </div>
      </div>
    );
  }

  return (
    <div className="clist-item">
      {/* 미리보기는 카드와 같은 규칙 — 맛보기 0번째(사용자 확정) */}
      <Thumb image={p.image} name={p.name} href={href}
        previewCcid={hoverPreview ? p.previews[0]?.curriculumId : undefined} />
      <div className="clist-info mr20">
{/* 추천 배지 없음 — 카드와 같다(2026-08-23 사용자 지시) */}
        <div className="clist-badges mb5">
          <span className={`badge-type ${badgeClass(p.type)} filter`}>{typeLabel(p.type, t)}</span>
        </div>
        <p className="clist-title"><SmartLink href={href}>{p.name}</SmartLink></p>
        {(teacher || category) && (
          <p className="course-teacher mt3">
            <Icon name="user" /> {[teacher?.name, category].filter(Boolean).join(' · ')}
          </p>
        )}
        <CourseChips p={p} className="mt5" />
      </div>
      <div className="clist-links">
        {/* 리스트는 래퍼가 없다 — 펼침이 `clist-links` 기준으로 열린다 */}
        <CourseLinks p={p} freeVod={freeVod} teacherName={teacher?.name} />
      </div>
      {/* 찜은 링크 줄이 아니라 `clist-actions` 첫 자식이다(2026-08-25 가이드 개정) */}
      <div className="clist-actions">
        <WishButton productId={p.id} wished={p.wished} className="clist-wish" />
        <PriceOptions p={p} lines={price.lines} picked={price.picked} onPick={price.setPicked}
          showSingle={priceDetail} withDiscount />
        <PriceFooter billing={billing} p={p} line={price.line} href={href} block />
      </div>
    </div>
  );
}
